import { Input } from "@vds/inputs";
import { DropdownSelect } from "@vds/selects";
import { Body } from "@vds/typography";
import { ErrorMessage, Field, Form, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { GetCity, GetCountries, GetState } from "react-country-state-city";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { trimNameField, validateNameField } from "../sitesUtil";
import styles from "./basicForm.module.css";
import { NAMING_VALIDATION_ERROR } from "../../../global/constants/errorConstants";
import { COUNTRY_SRC } from "../../../global/constants/constants";
export const BasicForm = ({
  isEditable,
  onFloorChange,
  onValidationChange,
  mainCityDropdownDisplay,
  setIsSiteDuplicateErrorYes,
  setIsSiteDuplicateErrorNo,
  setIsCampusDuplicateError,
}) => {
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [isError, setIsError] = useState(false);
  const dispatch = useDispatch();
  const [isCampusOrSiteIsAlreadyCreated, setCampusOrSiteIsAlreadyCreated] =
    useState(false);
  const [duplicateCampusError, setDuplicateCampusError] = useState("");
  const [duplicateSiteError, setDuplicateSiteError] = useState("");
  const [siteNameValidationError, setSiteNameValidationError] = useState("");
  const location = useLocation();
  const mode = location?.state?.mode || "add";
  const record = location?.state?.record;
  const recordCampusTagId = record?.campusTagId;
  const isSiteFromCampus = record?.isSiteFromCampus;
  const fromCampusDet = location?.state?.fromCampusPage || false;
  const isDataFromUCPage = !!fromCampusDet && mode === "add";
  const sitesData = siteTagList || "{}";
  const campuses = sitesData?.campuses;
  const sites = sitesData?.sites;
  const cities = sitesData?.cities;
  const states = sitesData?.states;
  const countries = sitesData?.countries;

  let campusOptions = [{ value: "new", label: "I want to create a new one" }];
  if (campuses && campuses.length > 0) {
    campusOptions = campusOptions.concat(
      campuses.map((campus) => ({
        value: campus.id,
        label: campus.name,
      }))
    );
  }

  const {
    values: {
      isCampusSite,
      campusNameId,
      newCampusName,
      siteName,
      country,
      state,
      city,
      siteDescription,
    },
    setFieldValue,
  } = useFormikContext();
  const countryName = country?.label;
  const stateName = state?.label;
  const cityName = city?.label;
  const cityValue = city?.value;
  const countryValue = country?.value;
  const stateValue = state?.value;
  const campusNameValue = campusNameId?.value;

  useEffect(() => {
    async function fetchCountries() {
      try {
        const result = await GetCountries(COUNTRY_SRC);
        if (result?.length) {
          setCountryList(
            result
              .filter((country) => country.name === "United States")
              .map((country) => ({
                label: country.name,
                value: country.id,
              }))
          );
        }
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    if (campusNameValue && campusNameValue !== "new" && isCampusSite !== "0") {
      const campus = campuses.find((c) => c.id === campusNameValue);
      if (campus) {
        setCampusOrSiteIsAlreadyCreated(true);
        setDuplicateCampusError("");
        setIsCampusDuplicateError(false);
        setDuplicateSiteError("");
        setIsSiteDuplicateErrorNo(false);
      } else {
        setCampusOrSiteIsAlreadyCreated(false);
      }
    } else {
      setCampusOrSiteIsAlreadyCreated(false);
    }
  }, [campusNameValue, isCampusSite]);

  useEffect(() => {
    if (mode === "edit" && !isEditable) return;

    if (countryValue) {
      async function fetchStates() {
        const states = await GetState(countryValue,COUNTRY_SRC);
        setStateList(
          (states || []).map((s) => ({ value: s.id, label: s.name }))
        );
      }
      fetchStates();
    }
  }, [countryValue, mode, isEditable]);

  useEffect(() => {
    if (mode === "edit" && !isEditable) return;

    if (countryValue && stateValue) {
      async function fetchCities() {
        try {
          const cities = await GetCity(countryValue, stateValue,COUNTRY_SRC);
          setCityList(
            (cities || []).map((c) => ({ value: c.id, label: c.name }))
          );
        } catch (err) {
          console.error("Failed to fetch cities:", err);
        }
      }
      fetchCities();
    }
  }, [countryValue, stateValue, mode, isEditable]);

  useEffect(() => {
    if (countryName !== "Select a Country" && countryList.length > 0) {
      const countryOption = countryList.find(
        (country) => country.label === countryName
      );
      setFieldValue("country", countryOption);
    }
  }, [countryList, countryName]);
  useEffect(() => {
    if (
      stateList.length > 0 &&
      stateName !== "Select a State" &&
      stateName !== undefined
    ) {
      const stateOption = stateList.find((state) => state.label === stateName);
      setFieldValue("state", stateOption);
    }
  }, [stateList, stateName]);
  useEffect(() => {
    if (
      cityList.length > 0 &&
      cityName !== "Select a City" &&
      cityName !== undefined
    ) {
      const cityOption = cityList.find((city) => city.label === cityName);
      setFieldValue("city", cityOption || { label: "", value: "" });
    }
  }, [cityList, cityName]);

  useEffect(() => {
    if (mode === "edit" && !isEditable && record) {
      setFieldValue("siteName", record?.siteTagName);
      setFieldValue(
        "siteDescription",
        record.description === undefined || record.description === null
          ? ""
          : record?.description
      );
      setFieldValue("state", {
        label: record?.stateTagName,
        value: record?.stateTagId,
      });
      setFieldValue("city", {
        label: record?.cityTagName,
        value: record?.cityTagId,
      });

      setStateList([
        { label: record?.stateTagName, value: record?.stateTagId },
      ]);
      setCityList([{ label: record?.cityTagName, value: record?.cityTagId }]);
    }
  }, [mode, isEditable, record, setFieldValue, setStateList, setCityList]);

  useEffect(() => {
    if (
      mode === "add" &&
      record?.sitename &&
      campusOptions.length > 1 &&
      !campusOptions.some((option) => option.value === campusNameId)
    ) {
      const matchingCampus = campusOptions.find(
        (option) => option.label === record.sitename
      );
      if (matchingCampus) {
        setFieldValue("campusNameId", matchingCampus.value);
        checkCampusOrSiteIsCreated(matchingCampus.value);
      }
    }
  }, [campusOptions.length, mode, record?.sitename, setFieldValue]);

  const matchingRecordCampus =
    recordCampusTagId &&
    campusOptions.find((option) => option.value === recordCampusTagId);
  const matchingRecordCampusValue = matchingRecordCampus?.value;
  const matchingRecordCampusName = matchingRecordCampus?.label;
  useEffect(() => {
    if (isDataFromUCPage && matchingRecordCampusValue) {
      const campusNameId = {
        label: matchingRecordCampusName,
        value: matchingRecordCampusValue,
      };
      setFieldValue("newCampusName", matchingRecordCampusName);
      setFieldValue("campusNameId", campusNameId);
      checkCampusOrSiteIsCreated({
        target: { value: matchingRecordCampusName },
      });
    }
  }, [isDataFromUCPage, matchingRecordCampusValue]);

  useEffect(() => {
    setIsSiteDuplicateErrorNo(false);
    setIsSiteDuplicateErrorYes(false);
  }, [isCampusSite]);

  const handleCountry = async (selectedOption) => {
    if (selectedOption?.target.value) {
      const selectedCountryOption = countryList.find(
        (country) => country.label === selectedOption.target.value
      );
      setFieldValue("country", selectedCountryOption);
      setFieldValue("state", { label: "", value: "" });
      setFieldValue("city", { label: "", value: "" });
    }
  };

  const handleState = async (selectedOption) => {
    if (selectedOption?.target.value) {
      const selectedStateOption = stateList.find(
        (state) => state.label === selectedOption.target.value
      );
      setFieldValue("state", selectedStateOption);
      setFieldValue("city", { label: "", value: "" });
    }
  };

  const handleCity = async (selectedOption) => {
    if (selectedOption?.target.value) {
      const selectedCityOption = cityList.find(
        (city) => city?.label === selectedOption?.target?.value
      );
      setFieldValue("city", selectedCityOption);
    }
  };

  const checkCampusOrSiteIsCreated = (value) => {
    // setStoreSelectedValue(false);
    const selectedCampus = campusOptions.find(
      (campus) => campus.label === value?.target?.value
    );

    setCampusOrSiteIsAlreadyCreated(true);
    const matchingCampusTag = campuses.find(
      (c) => c.id === selectedCampus?.value
    );
    setCityList([]);
    setStateList([]);
    if (matchingCampusTag) {
      const matchingCityTag = cities.find(
        (c) => c.id === matchingCampusTag?.tagids?.[0]
      );
      const matchingStateTag =
        matchingCityTag &&
        states.find((s) => s.id === matchingCityTag?.tagids?.[0]);
      const matchingCountryTag =
        matchingStateTag &&
        countries.find((c) => c.id === matchingStateTag?.tagids?.[0]);
      if (matchingCountryTag && matchingStateTag && matchingCityTag) {
        setFieldValue("country", {
          label: matchingCountryTag.name,
          value: countryList?.[0]?.value,
        });
        setFieldValue("state", {
          label: matchingStateTag.name,
          value: null,
        });
        setFieldValue("city", {
          label: matchingCityTag.name,
          value: null,
        });
      } else {
        setCampusOrSiteIsAlreadyCreated(false);
      }
    } else {
      setCampusOrSiteIsAlreadyCreated(false);
    }
  };

  const handleSiteNameEntered = (e) => {
    const rawValue = e.target.value;
    setFieldValue("siteName", rawValue);
    const siteNameEntered = rawValue;

    // Validate format - check raw value to show error for invalid characters
    if (rawValue && !validateNameField(rawValue)) {
      setIsError(true);
      setSiteNameValidationError(NAMING_VALIDATION_ERROR);
      onValidationChange(true);
      return;
    } else {
      setIsError(false);
      setSiteNameValidationError("");
    }

    const isSiteDuplicate = sites?.some(
      (site) => trimNameField(site.name).toLowerCase() === trimNameField(siteNameEntered).toLowerCase()
    );

    if (isSiteDuplicate && isCampusSite === "1") {
      setIsError(true);
      setIsSiteDuplicateErrorYes(true);
      onValidationChange(true);
      setDuplicateSiteError("Please enter a unique Site name");
    } else if (!isSiteDuplicate && isCampusSite === "1") {
      setIsError(false);
      setIsSiteDuplicateErrorYes(false);
      onValidationChange(false);
      setDuplicateSiteError("");
    } else if (isSiteDuplicate && isCampusSite === "0") {
      setIsError(true);
      setIsSiteDuplicateErrorNo(true);
      onValidationChange(true);
      setDuplicateSiteError("Please enter a unique Site name");
    } else if (!isSiteDuplicate && isCampusSite === "0") {
      setIsError(false);
      setIsSiteDuplicateErrorNo(false);
      setIsCampusDuplicateError(false);
      onValidationChange(false);
      setDuplicateSiteError("");
    }

    Boolean(
      mode !== "edit" && (isCampusSite !== "1" || campusNameId?.value === "new")
    )
      ? setCampusOrSiteIsAlreadyCreated(false)
      : setCampusOrSiteIsAlreadyCreated(true);
  };

  const handleCampusNameEntered = (e) => {
    const rawValue = e.target.value;
    setFieldValue("newCampusName", rawValue);
    const campusNameEntered = rawValue;

    // Validate format - check raw value to show error for invalid characters
    if (rawValue && !validateNameField(rawValue)) {
      setIsError(true);
      setIsCampusDuplicateError(true);
      onValidationChange(true);
      setDuplicateCampusError(NAMING_VALIDATION_ERROR);
      return;
    }
    const isCampusDuplicate = campuses?.some(
      (campus) =>
        trimNameField(campus?.name).toLowerCase() === trimNameField(campusNameEntered).toLowerCase()
    );
    if (isCampusDuplicate && isCampusSite === "1") {
      setIsError(true);
      setIsCampusDuplicateError(true);
      onValidationChange(true);
      setDuplicateCampusError("Please enter a unique Campus name");
    } else if (!isCampusDuplicate && isCampusSite === "1") {
      setIsError(false);
      setIsCampusDuplicateError(false);
      onValidationChange(false);
      setDuplicateCampusError("");
    }
  };

  return (
    <Form
      className={`d-flex flex-column gap-2 w-100`}
      style={{ overflow: "visible" }}
    >
      <div
        className="d-flex flex-column p-2 gap-3"
        style={{
          width: "100%",
          maxWidth: "600px",
          maxHeight: "630px",
          overflowY: "auto",
          overflowX: "hidden"
        }}
      >
        {mode === "add" && (
          <div className="d-flex flex-column">
            <Body size="medium"> Is this site a part of a campus?</Body>

            <div className="d-flex flex-column">
              <label
                className={`d-flex gap-3 align-items-center ${styles.formInput} ${styles.h25}`}
              >
                <Field
                  type="radio"
                  name="isCampusSite"
                  value={"0"}
                  className={styles.formInput}
                  disabled={fromCampusDet}
                />
                No
              </label>
              <label
                className={`d-flex gap-3 align-items-center ${styles.formInput} ${styles.h25}`}
              >
                <Field
                  type="radio"
                  name="isCampusSite"
                  value={"1"}
                  className={styles.formInput}
                  disabled={fromCampusDet}
                />
                Yes
              </label>
            </div>
            <ErrorMessage
              name="isCampusSite"
              component={"div"}
              className={styles.errorLabel}
            />
          </div>
        )}

        {Boolean(mode !== "edit" && isCampusSite === "1") && (
          <>
            <div className="d-flex flex-column ">
              {mode === "add" ? (
                <DropdownSelect
                  label="Campus name"
                  errorText="Select a Campus"
                  error={false}
                  inlineLabel={false}
                  value={campusNameId?.label}
                  data-testid="basicForm_campusNameDropdownSelect"
                  onChange={
                    fromCampusDet
                      ? () => {}
                      : (option) => {
                          const selectedcCampusOptions = campusOptions.find(
                            (campus) => campus?.label === option?.target?.value
                          );
                          setFieldValue("campusNameId", selectedcCampusOptions);
                          if (selectedcCampusOptions?.value === "new") {
                            setFieldValue("country", { label: "", value: "" });
                            setFieldValue("state", { label: "", value: "" });
                            setFieldValue("city", { label: "", value: "" });
                          }
                          setIsCampusDuplicateError(false);
                          option?.value !== "new"
                            ? checkCampusOrSiteIsCreated(option)
                            : setCampusOrSiteIsAlreadyCreated(false);
                        }
                  }
                  options={campusOptions}
                  disabled={
                    (mode === "edit" && !isEditable) || isDataFromUCPage
                  }
                >
                  {campusOptions?.length > 0 && (
                    <>
                      <option value={""}></option>
                      {[
                        ...new Map(
                          campusOptions.map((item) => [item?.["value"], item])
                        )?.values(),
                      ]?.map((item) => (
                        <option value={item?.label}>{item?.label}</option>
                      ))}
                    </>
                  )}
                </DropdownSelect>
              ) : (
                <Input
                  type="text"
                  label="Campus name"
                  readOnly={false}
                  required={true}
                  disabled={false}
                  error={false}
                  errorText=""
                  value={
                    campusOptions?.find(
                      (campus) => campus?.value === campusNameId?.value
                    )?.label || ""
                  }
                />
              )}
              <ErrorMessage
                name="campusNameId"
                component={"div"}
                style={{ color: "darkred" }}
              />
            </div>
            {campusNameId?.value === "new" && (
              <div className="d-flex flex-column ">
                <Input
                  type="text"
                  label="New campus name"
                  readOnly={false}
                  required={true}
                  disabled={false}
                  error={!!duplicateCampusError}
                  errorText={duplicateCampusError}
                  value={newCampusName}
                  data-testid="basicForm_newCampusNameInput"
                  onChange={handleCampusNameEntered}
                  isDisabled={mode === "edit" && !isEditable}
                />
                <ErrorMessage
                  name="newCampusName"
                  component={"div"}
                  style={{ color: "darkred" }}
                />
              </div>
            )}
          </>
        )}

        <div className="d-flex flex-column ">
          <Input
            type="text"
            label="Site name"
            readOnly={false}
            required={true}
            disabled={mode === "edit" && !isEditable}
            error={!!duplicateSiteError || !!siteNameValidationError}
            errorText={siteNameValidationError || duplicateSiteError}
            value={siteName}
            data-testid="basicForm_siteNameInput"
            onChange={handleSiteNameEntered}
            isDisabled={mode === "edit" && !isEditable}
          />
          <ErrorMessage
            name="siteName"
            component={"div"}
            style={{ color: "darkred" }}
          />
        </div>

        {Boolean(mode !== "edit") && (
          <div className="d-flex flex-column ">
            <DropdownSelect
              label="Country"
              errorText="Select a country"
              error={false}
              inlineLabel={false}
              value={country?.label}
              data-testid="basicForm_countryDropdownSelect"
              onChange={
                isCampusOrSiteIsAlreadyCreated ||
                fromCampusDet ||
                (mode === "edit" && !isEditable)
                  ? () => {}
                  : (e) => handleCountry(e)
              }
              options={countryList}
              disabled={
                (mode === "edit" && !isEditable) ||
                fromCampusDet ||
                (isCampusOrSiteIsAlreadyCreated &&
                  isCampusSite !== "0" &&
                  campusNameId?.value !== "new")
              }
            >
              {countryList?.length > 0 && (
                <>
                  <option value={""}></option>
                  {[
                    ...new Map(
                      countryList.map((item) => [item["value"], item])
                    ).values(),
                  ]?.map((item) => (
                    <option value={item?.label}>{item?.label}</option>
                  ))}
                </>
              )}
            </DropdownSelect>
            <ErrorMessage
              name="country"
              component={"div"}
              style={{ color: "darkred" }}
            />
          </div>
        )}
        <div className="d-flex flex-column ">
          <DropdownSelect
            label="State"
            errorText="Select a state"
            error={false}
            inlineLabel={false}
            value={state?.label}
            data-testid="basicForm_stateDropdownSelect"
            onChange={
              (mode === "add" && isCampusOrSiteIsAlreadyCreated) ||
              fromCampusDet || isSiteFromCampus ||
              (mode === "edit" && !isEditable)
                ? onFloorChange
                : (e) => {
                    handleState(e);
                    onFloorChange();
                  }
            }
            options={stateList}
            disabled={
              (mode === "edit" && !isEditable) ||
              fromCampusDet || isSiteFromCampus ||
              (isCampusOrSiteIsAlreadyCreated &&
                isCampusSite !== "0" &&
                campusNameId?.value !== "new")
            }
          >
            {stateList?.length > 0 && (
              <>
                <option value={""}></option>
                {[
                  ...new Map(
                    stateList.map((item) => [item["value"], item])
                  ).values(),
                ]?.map((item) => (
                  <option value={item?.label}>{item?.label}</option>
                ))}
              </>
            )}
          </DropdownSelect>
          <ErrorMessage
            name="state"
            component={"div"}
            style={{ color: "darkred" }}
          />
        </div>

        <div className="d-flex flex-column ">
          <DropdownSelect
            label="City"
            errorText="Select a city"
            error={false}
            inlineLabel={false}
            value={city?.label}
            data-testid="basicForm_cityDropdownSelect"
            onChange={
              (mode === "add" && isCampusOrSiteIsAlreadyCreated) ||
              fromCampusDet || isSiteFromCampus ||
              (mode === "edit" && !isEditable)
                ? () => {
                  onFloorChange()
                }
                : (e) => {
                    handleCity(e);
                    onFloorChange();
                  }
            }
            options={cityList}
            disabled={
              (mode === "edit" && !isEditable) ||
              fromCampusDet || isSiteFromCampus ||
              (isCampusOrSiteIsAlreadyCreated &&
                isCampusSite !== "0" &&
                campusNameId?.value !== "new")
            }
          >
            {cityList?.length > 0 && (
              <>
                <option value={""}></option>
                {[
                  ...new Map(
                    cityList.map((item) => [item["value"], item])
                  ).values(),
                ]?.map((item) => (
                  <option value={item?.label}>{item?.label}</option>
                ))}
              </>
            )}
          </DropdownSelect>
          <ErrorMessage
            name="city"
            component={"div"}
            style={{ color: "darkred" }}
          />
        </div>

        <div className="d-flex flex-column">
          <Input
            type="text"
            label="Site description"
            readOnly={false}
            required={false}
            disabled={mode === "edit" && !isEditable}
            error={false}
            value={siteDescription}
            data-testid="basicForm_siteDescriptionInput"
            onChange={(e) => {
              setFieldValue("siteDescription", e.target.value);
            }}
          />
        </div>
      </div>
    </Form>
  );
};
